import * as Sentry from "@sentry/react";
import type { ChangeEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ListCard, ListRow } from "@/components/ui/list-card";
import { Spinner } from "@/components/ui/spinner";

import { LawFirmsAPI } from "../../api/lawFirms";
import type { LawFirm } from "../../api/types/lawFirm";
import { FirmContactSection } from "./FirmContactSection";
import { FirmSidebarSection } from "./FirmSidebarSection";
import { FirmTimezoneSection } from "./FirmTimezoneSection";

type Props = {
  lawFirmId: string | null;
};

export function GeneralTab({ lawFirmId }: Props) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [lawFirm, setLawFirm] = useState<LawFirm | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!lawFirmId) {
        setLawFirm(null);
        return;
      }

      setIsLoading(true);
      try {
        const payload = await LawFirmsAPI.getById(lawFirmId);
        if (!cancelled) setLawFirm(payload);
      } catch (error) {
        Sentry.captureException(error);
        if (!cancelled) setLawFirm(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [lawFirmId]);

  const handleLogoFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";
    if (!lawFirmId || !file) return;

    setIsUploading(true);
    try {
      await LawFirmsAPI.uploadLogo(lawFirmId, file);
      const refreshedFirm = await LawFirmsAPI.getById(lawFirmId);
      setLawFirm(refreshedFirm);
      toast.success("Logo updated", {
        description: "Your law firm logo was uploaded successfully."
      });
    } catch (error) {
      Sentry.captureException(error);
      const message =
        error instanceof Error
          ? error.message
          : "Failed to upload law firm logo.";
      toast.error("Couldn’t upload logo", { description: message });
    } finally {
      setIsUploading(false);
    }
  };

  const lawFirmName = lawFirm?.name?.trim() || "Law firm";
  const initials = lawFirmName.slice(0, 1).toUpperCase() || "L";
  const isBusy = isLoading || isUploading;

  return (
    <div className="flex w-full flex-col gap-6 px-6 pt-4 pb-8">
      <ListCard>
        <ListRow
          media={
            <Avatar size="lg" shape="square">
              {lawFirm?.logo_url ? (
                <AvatarImage src={lawFirm.logo_url} alt="" />
              ) : null}
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
          }
          title={lawFirmName}
          description={
            <span className="text-sm text-fg-3">
              Upload a PNG or JPG logo up to 2 MB.
            </span>
          }
          control={
            <Button
              variant="secondary"
              size="sm"
              disabled={!lawFirmId || isBusy}
              onClick={() => fileRef.current?.click()}
            >
              {isBusy ? <Spinner /> : null}
              Upload Logo
            </Button>
          }
        />
      </ListCard>

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg"
        onChange={handleLogoFileChange}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
      />

      <FirmTimezoneSection lawFirm={lawFirm} onSaved={setLawFirm} />

      <FirmSidebarSection lawFirm={lawFirm} onSaved={setLawFirm} />

      <FirmContactSection lawFirm={lawFirm} onSaved={setLawFirm} />
    </div>
  );
}
